// "What do you want within reach?", asked once per surface, in a dropdown.
//
// This replaced a mode. The bar used to turn itself into a row of switches when
// a ★ was pressed, which meant the thing you were editing was also the thing
// you were editing *with*: the row changed shape under the cursor, items moved,
// and there was nowhere to put anything that was not already on it. A list in a
// panel can show everything — including the screens that were never offered —
// without the bar moving at all.
//
// What can be pinned comes from `nav.ts` and from the connected accounts, so a
// screen this person may not see is not a screen they can pin, and a screen
// added to the navigation is offered here the same day.
//
// One of these is mounted per surface — the top bar and the rail keep separate
// lists, because they are separate shapes. The component does not know which it
// is beyond the words it is handed and where it is told to open.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AccountMark } from "../components/AccountMark";
import { StarIcon } from "./icons";
import type { FavouriteItem } from "./state";

interface Props {
  candidates: FavouriteItem[];
  isFavourite: (key: string) => boolean;
  toggle: (key: string) => void;
  customised: boolean;
  reset: () => void;
  /** Keys this surface holds whatever is chosen — offered ticked and fixed. */
  fixed: string[];
  /** How many are pinned right now. The button carries no visible word, so the
   *  count goes into its tooltip and its accessible name — an icon on its own
   *  has to say what it is when asked. */
  count: number;
  /** What this surface is called, in the words the person sees: "top bar",
   *  "rail". Used in the button's name and in the panel's first line, so two
   *  identical-looking stars are never ambiguous about which one they edit. */
  surfaceName: string;
  /** Where the panel hangs. The header's opens below and to the left; the
   *  rail's opens beside it, because there is nothing below the rail. */
  placement?: "below" | "beside";
}

export function FavouritesMenu({
  candidates, isFavourite, toggle, customised, reset, fixed, count, surfaceName,
  placement = "below",
}: Props) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  // Closing has to put the focus back where it came from, or a keyboard user is
  // returned to the top of the document every time they finish choosing.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", away);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", away);
    };
  }, [open]);

  const screens = candidates.filter((c) => c.kind === "nav");
  const accounts = candidates.filter((c) => c.kind === "account");

  return (
    <div className="fav-menu" ref={box}>
      <button
        ref={button}
        type="button"
        className={open ? "fav-menu-btn on" : "fav-menu-btn"}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Edit the ${surfaceName} — ${count} pinned`}
        onClick={() => setOpen((on) => !on)}
        title={`Choose the screens and accounts kept on the ${surfaceName}`}
      >
        <StarIcon className="nav-icon" />
      </button>

      {open && (
        <div className={placement === "beside" ? "fav-menu-panel card beside" : "fav-menu-panel card"}
             role="dialog" aria-label={`Choose what is on the ${surfaceName}`}>
          <p className="muted fav-menu-lede">
            Anything ticked here goes on the <strong>{surfaceName}</strong>. The other
            favourites surface keeps its own list.
          </p>

          <Section title="Screens" items={screens} fixed={fixed}
                   isFavourite={isFavourite} toggle={toggle} />

          {accounts.length > 0
            ? <Section title="Accounts" items={accounts} fixed={fixed}
                       isFavourite={isFavourite} toggle={toggle} />
            : (
              <div className="fav-menu-group">
                <h3>Accounts</h3>
                <p className="muted fav-menu-empty">
                  Nothing connected yet. <Link to="/connections">Connect an account</Link> and it
                  can go on the bar.
                </p>
              </div>
            )}

          <div className="fav-menu-foot">
            {customised
              ? <button type="button" className="linkish" onClick={reset}>
                  Back to just my accounts
                </button>
              : <span className="muted">Showing your connected accounts.</span>}
            <span className="spacer" />
            <span className="muted">{count} pinned</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({
  title, items, fixed, isFavourite, toggle,
}: {
  title: string;
  items: FavouriteItem[];
  fixed: string[];
  isFavourite: (key: string) => boolean;
  toggle: (key: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="fav-menu-group">
      <h3>{title}</h3>
      <ul>
        {items.map((item) => {
          const Icon = item.icon;
          const on = isFavourite(item.key);
          // Pinned by the surface rather than by the person. Shown, ticked and
          // disabled, with the reason beside it — a row that quietly refuses to
          // untick is worse than one that says it will not.
          const held = fixed.includes(item.key);
          return (
            <li key={item.key}>
              {/* A real checkbox under the row, so the whole row is the hit
                  target and the keyboard and screen reader get a checkbox. */}
              <label className={`fav-menu-row${on ? " on" : ""}${held ? " held" : ""}`}>
                <input type="checkbox" checked={on} disabled={held}
                       onChange={() => toggle(item.key)} />
                {item.connection
                  ? <AccountMark connection={item.connection} />
                  : Icon ? <Icon className="nav-icon" /> : null}
                <span className="fav-menu-text">
                  {/* The disclosure `nav.ts` carries for every screen, kept on
                      the control that puts it one press away. */}
                  <strong title={item.title}>{item.label}</strong>
                  <span className="muted">{held ? "Always here" : item.hint}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
